import React from 'react';
import {
    Box,
    Chip,
    IconButton,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import {
    EditNoteOutlined,
    InfoOutlined,
    WarningAmber,
} from '@mui/icons-material';
import { CTableWrapper, DeleteIcon, EditIcon } from '../../../../../dum';
import { formatDate, formatFloat, round } from '../../../../../../helpers';
import { ExpensesPageQuery } from '../../../../../../services';

type ExpenseDoc = ExpensesPageQuery['paginatedExpenses']['docs'][number];

interface IExpenseCard {
    expense: ExpenseDoc;
    onEdit: (expense: ExpenseDoc) => void;
    // Optional-details dialog. Distinct from onEdit, which opens the full upsert.
    onEditDetails?: (expense: ExpenseDoc) => void;
    onDelete: (expense: ExpenseDoc) => void;
}

// Middot separator between meta parts.
function MetaSep() {
    return (
        <Typography component={'span'} variant={'caption'} color={'text.disabled'}>
            ·
        </Typography>
    );
}

/**
 * Card body for one expense inside the Gastos CCardList. Laid out as a single
 * scannable row: an identity zone on the left (proveedor + a secondary meta
 * line with date, folio, resource and notes) and a right-aligned money grid
 * (Subtotal / Total / Saldo) whose values line up down the whole list. A
 * multi-resource breakdown table is appended below when needed.
 */
export function ExpenseCard({
    expense,
    onEdit,
    onEditDetails,
    onDelete,
}: IExpenseCard) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const subBaseColor = isDark ? 'rgba(59,130,246,0.08)' : '#eff6ff';
    const subStripeColor = isDark ? 'rgba(59,130,246,0.16)' : '#dbeafe';

    // Rounded to cents so float dust never hides "Pagado"; negative = overpaid.
    const saldo = round(
        expense.total_with_tax - expense.transfer_receipts_total,
    );
    const isTransferComplete = saldo === 0;

    // Payment-date hint next to an outstanding Saldo: info icon (tooltip shows
    // the date) when set, warning icon when still missing.
    const paymentDateIcon = expense.expected_payment_date ? (
        <Tooltip
            arrow
            title={`Fecha de pago: ${formatDate(expense.expected_payment_date)}`}
        >
            <InfoOutlined
                fontSize={'small'}
                color={'info'}
                sx={{ verticalAlign: 'middle' }}
            />
        </Tooltip>
    ) : (
        <Tooltip arrow title={'Sin fecha de pago asignada'}>
            <WarningAmber
                fontSize={'small'}
                color={'warning'}
                sx={{ verticalAlign: 'middle' }}
            />
        </Tooltip>
    );

    // One resource → show its name (and qty × price) inline; several → collapse
    // to "Varios" and keep the breakdown table below.
    const resources = expense.expense_resources;
    const resourceLabel =
        resources.length === 1
            ? resources[0].resource?.name ?? ''
            : resources.length > 1
            ? 'Varios'
            : '';

    // Only worth spelling out "(units × price)" for a single resource when both
    // numbers carry information — if either the quantity or the unit price is 1,
    // the breakdown is redundant (it just restates the other number), so hide it.
    const singleResource = resources.length === 1 ? resources[0] : null;
    const singleResourceNotes = singleResource?.notes?.trim() || '';
    const showUnitBreakdown =
        !!singleResource &&
        singleResource.units !== 1 &&
        (singleResource.unit_price ?? 0) !== 1;

    // Secondary meta line: date first (left), then folio and resource details.
    // Built as a list so separators only appear between present parts.
    const metaParts: React.ReactNode[] = [];
    metaParts.push(
        <Typography key={'date'} variant={'caption'} color={'text.secondary'}>
            {formatDate(expense.date)}
        </Typography>,
    );
    if (expense.require_external_code && !expense.external_code) {
        metaParts.push(
            <Tooltip key={'folio'} arrow title={'Folio requerido sin asignar'}>
                <WarningAmber
                    fontSize={'small'}
                    color={'warning'}
                    sx={{ verticalAlign: 'middle' }}
                />
            </Tooltip>,
        );
    } else if (expense.external_code) {
        metaParts.push(
            <Typography key={'folio'} variant={'caption'} color={'text.secondary'}>
                #{expense.external_code}
            </Typography>,
        );
    }
    // Internal code is a fallback identifier — only shown when there's no
    // external (supplier-issued) code to display instead.
    if (!expense.external_code && expense.internal_code) {
        metaParts.push(
            <Typography
                key={'internal'}
                variant={'caption'}
                color={'text.secondary'}
            >
                Folio interno {expense.internal_code}
            </Typography>,
        );
    }
    if (resourceLabel || singleResourceNotes) {
        metaParts.push(
            <Typography
                key={'resource'}
                variant={'caption'}
                color={'text.primary'}
                sx={{ fontWeight: 600 }}
            >
                {resourceLabel || 'Recurso'}
                {showUnitBreakdown && singleResource ? (
                    <Typography
                        component={'span'}
                        variant={'caption'}
                        color={'text.secondary'}
                        sx={{ fontWeight: 400, ml: 0.5 }}
                    >
                        ({formatFloat(singleResource.units)} ×{' '}
                        {formatFloat(singleResource.unit_price ?? 0)})
                    </Typography>
                ) : null}
                {singleResourceNotes ? (
                    <Typography
                        component={'span'}
                        variant={'caption'}
                        color={'text.secondary'}
                        sx={{ fontWeight: 400, ml: 0.75 }}
                    >
                        — {singleResourceNotes}
                    </Typography>
                ) : null}
            </Typography>,
        );
    }
    if (expense.notes) {
        metaParts.push(
            <Typography key={'notes'} variant={'caption'} color={'text.secondary'}>
                {expense.notes}
            </Typography>,
        );
    }
    // Audit stamp. Unlike the table pages there is no "—" placeholder here:
    // meta parts on this card are conditional by design (see above), and
    // every pre-audit expense would otherwise carry an empty label. Only the
    // updater is shown in lists; the creator lives on the upsert dialog.
    if (expense.updated_by) {
        metaParts.push(
            <Typography key={'updated_by'} variant={'caption'} color={'text.secondary'}>
                Actualizado por {expense.updated_by.fullname}
            </Typography>,
        );
    }

    return (
        <Box sx={{ opacity: expense.canceled ? 0.6 : 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                {/* Identity zone */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            flexWrap: 'wrap',
                        }}
                    >
                        <Typography variant={'subtitle2'} sx={{ fontWeight: 700 }}>
                            {expense.account?.compound_name}
                        </Typography>
                        {expense.receipt_type ? (
                            <Chip
                                size={'small'}
                                variant={'outlined'}
                                label={expense.receipt_type.name}
                            />
                        ) : null}
                        {expense.generated_from_expense_id != null ? (
                            <Chip
                                size={'small'}
                                variant={'outlined'}
                                color={'info'}
                                label={'Recurrente'}
                            />
                        ) : null}
                        {expense.canceled ? (
                            <Chip
                                size={'small'}
                                color={'error'}
                                label={'Cancelada'}
                            />
                        ) : null}
                        {expense.reconciliation_only ? (
                            <Chip
                                size={'small'}
                                color={'warning'}
                                variant={'outlined'}
                                label={'Solo conciliación'}
                            />
                        ) : null}
                        {expense.is_draft ? (
                            <Chip
                                size={'small'}
                                variant={'outlined'}
                                label={'Borrador'}
                            />
                        ) : null}
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: 0.75,
                            mt: 0.25,
                        }}
                    >
                        {metaParts.map((part, index) => (
                            <React.Fragment key={index}>
                                {index > 0 ? <MetaSep /> : null}
                                {part}
                            </React.Fragment>
                        ))}
                    </Box>
                </Box>

                {/* Money grid — labels and values line up down the list */}
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'max-content max-content',
                        columnGap: 1.5,
                        rowGap: 0.25,
                        alignItems: 'baseline',
                        justifyContent: 'end',
                        flexShrink: 0,
                    }}
                >
                    <Typography variant={'caption'} color={'text.secondary'}>
                        Subtotal
                    </Typography>
                    <Typography variant={'body2'} sx={{ textAlign: 'right' }}>
                        {formatFloat(expense.subtotal)}
                    </Typography>

                    <Typography variant={'caption'} color={'text.secondary'}>
                        Total
                    </Typography>
                    <Typography
                        variant={'subtitle2'}
                        sx={{ fontWeight: 700, textAlign: 'right' }}
                    >
                        {formatFloat(expense.total_with_tax)}
                    </Typography>

                    <Typography variant={'caption'} color={'text.secondary'}>
                        Saldo
                    </Typography>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            gap: 0.5,
                        }}
                    >
                        {isTransferComplete ? (
                            <Typography
                                variant={'body2'}
                                color={'success.main'}
                                sx={{ fontWeight: 600 }}
                            >
                                Pagado
                            </Typography>
                        ) : (
                            <>
                                <Typography
                                    variant={'body2'}
                                    color={'warning.main'}
                                    sx={{ fontWeight: 600 }}
                                >
                                    {formatFloat(saldo)}
                                </Typography>
                                {paymentDateIcon}
                            </>
                        )}
                    </Box>
                </Box>

                {/* Actions */}
                <Box sx={{ display: 'flex', flexShrink: 0 }}>
                    {onEditDetails ? (
                        <Tooltip arrow title={'Editar datos opcionales'}>
                            <IconButton
                                size={'small'}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onEditDetails(expense);
                                }}
                            >
                                <EditNoteOutlined />
                            </IconButton>
                        </Tooltip>
                    ) : null}
                    <Tooltip arrow title={'Editar'}>
                        <IconButton
                            size={'small'}
                            onClick={(e) => {
                                // The whole card is clickable (opens edit) — keep
                                // the buttons from double-firing through it.
                                e.stopPropagation();
                                onEdit(expense);
                            }}
                        >
                            <EditIcon />
                        </IconButton>
                    </Tooltip>
                    <IconButton
                        size={'small'}
                        disabled={!expense.is_deletable}
                        onClick={(e) => {
                            e.stopPropagation();
                            onDelete(expense);
                        }}
                    >
                        <DeleteIcon />
                    </IconButton>
                </Box>
            </Box>

            {resources.length > 1 ? (
                <Box sx={{ mt: 1 }}>
                    <CTableWrapper
                        padding={false}
                        baseColor={subBaseColor}
                        stripeColor={subStripeColor}
                        items={[
                            {
                                title: 'Producto/Servicio',
                                cellGrow: 40,
                                textAlign: 'left',
                                content: (er) => er.resource?.name,
                            },
                            {
                                title: 'Unidades',
                                cellGrow: 20,
                                textAlign: 'right',
                                content: (er) => formatFloat(er.units),
                            },
                            {
                                title: 'Precio',
                                cellGrow: 20,
                                textAlign: 'right',
                                content: (er) => formatFloat(er.unit_price ?? 0),
                            },
                            {
                                title: 'Total',
                                cellGrow: 20,
                                textAlign: 'right',
                                content: (er) =>
                                    formatFloat(er.units * (er.unit_price ?? 0)),
                            },
                            {
                                title: 'Notas',
                                cellGrow: 40,
                                textAlign: 'right',
                                content: (er) => er.notes,
                            },
                        ]}
                        data={resources}
                    />
                </Box>
            ) : null}
        </Box>
    );
}
